import React, { /* useContext, */ useState, useEffect, useRef } from "react";
import axios from "../../../../api/axios";
import { uploadCustomAssetRoute, filesRoute } from "../../../../api/routes";
import DebugLogger from "../../../../helpers/DebugLogger";
import { slideOutPanelControl } from "../SlideoutPanel";
import { convertUniqueFilenameToOriginal, getCampaignImages } from "../../../../components/v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";
import TabbedPanel from "../../../../components/TabbedPanel/TabbedPanel";
import './ImageUploaderContent.scss';

const ImageUploaderContent = (props) => {
    const [busy, setBusy] = useState(false);
    const [previewImg, setPreviewImg] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageFileData, setImageFileData] = useState(props.imageFileData ?? null);
    const imageInputRef = useRef(null);
    const [currentTab, setCurrentTab] = useState('upload');
    const [campaignImages, setCampaignImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const hasFetchedImagesRef = useRef(false);

    // Update local state when props change
    useEffect(() => {
        setImageFileData(props.imageFileData ?? null);
        setPreviewImg(props.imageFileData?.srcUrl ?? null);
    }, [props.imageFileData]);

    // Track image dimensions for display
    const [imageDimensions, setImageDimensions] = useState({width: 'unknown', height: 'unknown'});
    useEffect(() => {
        setImageDimensions({width: 'unknown', height: 'unknown'});
        if (previewImg) {
            const img = new Image();
            img.onload = () => {
                setImageDimensions({width: img.width, height: img.height});
            }
            img.src = previewImg;
        }
    }, [previewImg]);

    // Reset fetch flag when campaign changes so the library refreshes for a different campaign
    useEffect(() => {
        hasFetchedImagesRef.current = false;
        setCampaignImages([]);
    }, [props.campaignData?._id]);

    // Load campaign images when switching to library tab (fetch once per campaign)
    useEffect(() => {
        if (currentTab !== 'library' || !props.campaignData?._id || hasFetchedImagesRef.current) return;

        hasFetchedImagesRef.current = true;
        setLoadingImages(true);
        getCampaignImages(props.campaignData._id)
            .then(images => setCampaignImages(images))
            .catch(err => DebugLogger.error('Failed to load campaign images:', err))
            .finally(() => setLoadingImages(false));
    }, [currentTab, props.campaignData?._id]);

    const chooseFile = () => {
        if (!busy && props.isOpenCanMakeChanges) {
            imageInputRef.current.click();
        }
    };

    const handlePhoto = async (e) => {
        e.preventDefault();
        if (!busy && props.isOpenCanMakeChanges) {
            if (e.target.files.length === 0) {
                return;
            }
            const image = e.target.files[0];
            
            // Create preview
            const previewUrl = URL.createObjectURL(image);
            setPreviewImg(previewUrl);
            setSelectedFile(image);

            // Auto-upload immediately
            DebugLogger.log('ImageUploaderContent: Auto-uploading image to server:', image);
            setBusy(true);

            try {
                const formData = new FormData();
                // Fields must come before file so multer-s3 has req.body populated in key()
                formData.append('filename', image.name);
                formData.append('type', 'image');
                if (props.campaignData?._id) {
                    formData.append('campaignID', props.campaignData._id);
                }
                formData.append('file', image);

                const response = await axios.post(uploadCustomAssetRoute, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                DebugLogger.log('ImageUploaderContent: Upload successful:', response.data);

                const uploadedFilename = response.data.uploadedFilename;
                const uploadType = response.data.type;

                // storedFilename is what gets saved to DB.
                // fixSrcUrls reconstructs the full URL as:
                //   CloudFront/feature-assets/campaigns/{id}/{type}/{storedFilename}
                // so we must store only the basename, not the full S3 key.
                let imageUrl;
                let storedFilename;

                if (uploadType === 'local') {
                    const campaignPath = props.campaignData?._id ? `campaigns/${props.campaignData._id}/` : '';
                    imageUrl = `${filesRoute}/feature-assets/${campaignPath}images/${uploadedFilename}`;
                    storedFilename = uploadedFilename;
                } else {
                    // S3 returns the full key (e.g. feature-assets/campaigns/{id}/image/Claw_Adidas.png)
                    // Store only the basename so fixSrcUrls builds the URL without duplication
                    storedFilename = uploadedFilename.split('/').pop();
                    imageUrl = uploadedFilename;
                }

                const uploadedImageData = {
                    srcUrl: imageUrl,
                    originalFile: image.name,
                    filename: storedFilename,
                    file: null
                };

                setImageFileData(uploadedImageData);
                setSelectedFile(null);

                /** KLUDGE FIX:
                 * We don't need to do anything else here except update the image data to update the filename and srcUrl.
                 * If we use the preview (blob:) URL we can make sure the end user sees the image immediately.
                 * The image is uploaded immediately, but we are not setting the newFile property so our saving logic doesn't try to re-upload it.
                 * 
                 * This needs some proper handling later on, but for now this makes this work as expected.
                 * 
                 * - Carl Trelfa 03/Feb/2026
                 */
                const updatedImageData = JSON.parse(JSON.stringify(props.imageFileData || {}));
                updatedImageData.srcUrl = previewUrl;
                updatedImageData.originalFile = image.name;
                updatedImageData.filename = storedFilename;
                updatedImageData.isDefault = false; // User uploaded a custom image - not a default asset

                // Trigger both callbacks to update UI and right sidebar preview
                if (props.onImageSelected) {
                    props.onImageSelected(updatedImageData);
                }
                if (props.onImageChanged) {
                    props.onImageChanged(updatedImageData);
                }

                setBusy(false);
                
                // Don't close the panel - keep it open for further editing
                
            } catch (error) {
                DebugLogger.error('ImageUploaderContent: Upload failed:', error);
                setBusy(false);
                alert('Failed to upload image. Please try again.');
                // Reset state on error
                setPreviewImg(props.imageFileData?.srcUrl ?? null);
                setSelectedFile(null);
                setImageFileData(props.imageFileData ?? null);
            }
        }
    };

    const handleRemove = () => {
        if (!busy && props.isOpenCanMakeChanges) {
            setPreviewImg(null);
            setSelectedFile(null);
            setImageFileData(null);

            // Only call the preview callback, NOT the confirm callback
            // The confirm callback seems to be removing the entire component
            if (props.onImageChanged) {
                props.onImageChanged(null);
            }

            // Don't close the panel - keep it open for further editing
        }
    };

    const handleLibraryImageSelect = (image) => {
        if (!busy && props.isOpenCanMakeChanges) {
            const imageData = {
                srcUrl: image.url,
                originalFile: image.name,
                filename: image.filename,
                file: null
            };

            setImageFileData(imageData);
            setPreviewImg(image.url);
            setSelectedFile(null);

            // Trigger both callbacks to update UI and right sidebar preview
            if (props.onImageSelected) {
                props.onImageSelected(imageData);
            }
            if (props.onImageChanged) {
                props.onImageChanged(imageData);
            }
        }
    };

    const handleTabSwitch = (tabId, tabData) => {
        setCurrentTab(tabData.id);
    };

    const tabData = [
        { label: 'Upload New', id: 'upload' },
        { label: 'Image Library', id: 'library' }
    ];

    const renderUploadTab = () => (
        <div className="upload-tab-content">
            {/* Preview Area - clickable for file selection */}
            <div 
                className={`image-preview-area${busy ? ' disabled' : ''}`}
                onClick={busy ? null : chooseFile}
                style={{ cursor: busy ? 'default' : 'pointer' }}
            >
                {previewImg ? (
                    <div className="image-preview-holder">
                        <img src={previewImg} alt={imageFileData?.originalFile || 'Preview'} />
                        {imageDimensions.width !== 'unknown' && (
                            <div className="image-dimensions-label">
                                {imageDimensions.width} x {imageDimensions.height}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="image-placeholder">
                        <div className="placeholder-text">
                            {busy ? 'Uploading...' : 'Click to select image'}
                        </div>
                    </div>
                )}
            </div>

            {/* File name display */}
            {imageFileData?.originalFile && (
                <div className="file-name">
                    {imageFileData.originalFile}
                </div>
            )}
            {/* Remove button - centered below filename */}
            {imageFileData && !busy && (
                <div className="remove-button-row">
                    <div 
                        className="clear-image-link"
                        onClick={handleRemove}
                    >
                        clear image
                    </div>
                </div>
            )}

            {/* Hidden file input */}
            <input
                type="file"
                accept=".png, .jpg, .jpeg, .gif"
                name="image"
                onChange={handlePhoto}
                hidden
                ref={imageInputRef}
            />
        </div>
    );

    const renderLibraryTab = () => (
        <div className="library-tab-content">
            {loadingImages ? (
                <div className="loading-message">Loading campaign images...</div>
            ) : campaignImages.length === 0 ? (
                <div className="no-items-message">
                    No images found in this campaign.
                    <br />Upload some images using the "Upload New" tab.
                </div>
            ) : (
                <div className="campaign-items-grid">
                    {campaignImages.map((image, index) => (
                        <div 
                            key={index} 
                            className={`campaign-library-item${imageFileData?.filename === image.filename ? ' selected' : ''}`}
                            onClick={() => handleLibraryImageSelect(image)}
                        >
                            <img src={image.url} alt={convertUniqueFilenameToOriginal(image.name)} />
                            <div className="item-name">{convertUniqueFilenameToOriginal(image.name)}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Show selected image details */}
            {imageFileData && currentTab === 'library' && (
                <div className="selected-item-info">
                    <div className="file-name">Selected: {convertUniqueFilenameToOriginal(imageFileData.originalFile)}</div>
                    {!busy && (
                        <div className="remove-button-row">
                            <div 
                                className="clear-image-link"
                                onClick={handleRemove}
                            >
                                clear selection
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="image-uploader-content">
            <TabbedPanel
                tabs={tabData}
                switchTabCallback={handleTabSwitch}
                initialTab={0}
                tabWidth="100%"
                tabStyle={2}
            >
                <>
                    {currentTab === 'upload' && renderUploadTab()}
                    {currentTab === 'library' && renderLibraryTab()}
                </>
            </TabbedPanel>
        </div>
    );
}

export default ImageUploaderContent;